import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { playerDisplayName, type TeamPublic, type TeamPlayerPublic, type CoachTeamPublic, type PlayerPublic, type CoachPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { ResponsiveTable } from "@/components/responsive-table";
import { Pencil, Trash2, Plus, UserPlus, Users, ChevronDown, ChevronRight, Megaphone, UserCircle } from "lucide-react";
import { ListBackButton } from "@/components/list-back-button";
import { PinButton } from "@/components/pin-button";
import { SendMessageButton } from "@/components/send-message-button";
import { EmergencyContactButton } from "@/components/emergency-contact-button";

const ageGroupLabels: Record<string, string> = {
  U14: "Under 14",
  U16: "Under 16",
  U18: "Under 18",
  Open: "Open",
};

export default function TeamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [team, setTeam] = useState<TeamPublic | null>(null);
  const [roster, setRoster] = useState<TeamPlayerPublic[]>([]);
  const [coaches, setCoaches] = useState<CoachTeamPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [coachesOpen, setCoachesOpen] = useState(true);
  const [rosterOpen, setRosterOpen] = useState(true);

  // Add player dialog state
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [availablePlayers, setAvailablePlayers] = useState<PlayerPublic[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");

  // Add coach dialog state
  const [showAddCoach, setShowAddCoach] = useState(false);
  const [availableCoaches, setAvailableCoaches] = useState<CoachPublic[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState("");

  // Publish to event dialog state (108)
  const [showPublish, setShowPublish] = useState(false);
  const [publishableEvents, setPublishableEvents] = useState<api.PublishableEventOption[]>([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [publishContent, setPublishContent] = useState("");
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    Promise.all([
      api.getTeam(id),
      api.getTeamPlayers(id),
      api.getTeamCoaches(id),
    ])
      .then(([teamRes, rosterRes, coachesRes]) => {
        setTeam(teamRes.team);
        setRoster(rosterRes.players);
        setCoaches(coachesRes.coaches);
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load team"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleAddPlayer() {
    if (!id || !selectedPlayerId) return;
    try {
      const res = await api.addTeamPlayer(id, selectedPlayerId);
      setRoster((prev) => [...prev, res.player]);
      setShowAddPlayer(false);
      setSelectedPlayerId("");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to add player");
    }
  }

  async function handleRemovePlayer(playerId: string) {
    if (!id || !confirm("Remove this player from the team?")) return;
    try {
      await api.removeTeamPlayer(id, playerId);
      setRoster((prev) => prev.filter((p) => p.player_id !== playerId));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to remove player");
    }
  }

  async function openAddPlayer() {
    if (!id) return;
    try {
      // Uses eligible-players endpoint: returns active, age-eligible players not already on team
      const res = await api.getEligiblePlayers(id);
      setAvailablePlayers(res.players);
      setShowAddPlayer(true);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load players");
    }
  }

  async function openAddCoach() {
    if (!team) return;
    try {
      const res = await api.getCoaches(team.school_id);
      // Filter out coaches already assigned
      const assignedIds = new Set(coaches.map((c) => c.coach_id));
      setAvailableCoaches(res.coaches.filter((c) => !assignedIds.has(c.id)));
      setShowAddCoach(true);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load coaches");
    }
  }

  async function handleAddCoach() {
    if (!id || !selectedCoachId) return;
    try {
      const res = await api.assignCoachToTeam(id, selectedCoachId);
      setCoaches((prev) => [...prev, res.coach]);
      setShowAddCoach(false);
      setSelectedCoachId("");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to assign coach");
    }
  }

  async function handleRemoveCoach(coachId: string) {
    if (!id || !confirm("Remove this coach from the team?")) return;
    try {
      await api.unassignCoachFromTeam(id, coachId);
      setCoaches((prev) => prev.filter((c) => c.coach_id !== coachId));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to remove coach");
    }
  }

  async function openPublish() {
    if (!id) return;
    setPublishError(null);
    setPublishContent("");
    setSelectedEventId("");
    try {
      const res = await api.getPublishableEvents(id);
      setPublishableEvents(res.events);
      setShowPublish(true);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load events");
    }
  }

  async function handlePublish() {
    if (!id || !selectedEventId || !publishContent.trim()) return;
    setPublishing(true);
    setPublishError(null);
    try {
      await api.createTeamPublication(id, {
        event_id: selectedEventId,
        content: publishContent.trim(),
      });
      setShowPublish(false);
    } catch (e) {
      setPublishError(e instanceof api.ApiError ? e.message : "Failed to publish");
    } finally {
      setPublishing(false);
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="team-detail" data-state="loading">Loading...</p>;
  if (error && !team) return <p className="text-sm text-destructive" data-page="team-detail" data-state="error" data-error>{error}</p>;
  if (!team) return <p className="text-muted-foreground" data-page="team-detail" data-state="empty">Team not found</p>;

  const canEdit = team._actions.includes("edit");
  const canManageRoster = team._actions.includes("manage_roster");
  const canSendMessage = team._actions.includes("send_message");

  return (
    <div className="flex flex-col gap-6" data-page="team-detail" data-state={error ? "error" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-region="header">
        <ListBackButton to="/teams" label="Teams" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">{team.display_name}</h1>
          <p className="text-muted-foreground">
            {ageGroupLabels[team.age_group] ?? team.age_group} &middot; {team.school_name}
            {team.sport_name && ` \u00b7 ${team.sport_name}`}
          </p>
        </div>
        <div className="flex gap-2">
          <PinButton pinType="team" entityType="Team" entityId={team.id} />
          {canSendMessage && (
            <SendMessageButton
              contextType="team"
              contextId={team.id}
              contextLabel={team.name}
              variant="outline"
              showLabel
            />
          )}
          {canManageRoster && (
            <Button
              size="sm"
              variant="outline"
              onClick={openPublish}
              data-action="publish-to-event"
            >
              <Megaphone className="mr-2 h-4 w-4" />
              Publish to Event
            </Button>
          )}
          {team._actions.includes("manage_positions") && team.sport_name && (
            <Link
              to={`/teams/${team.slug}/positions`}
              className={buttonVariants({ variant: "outline", size: "sm" })}
              data-action="positions"
            >
              <Users className="mr-2 h-4 w-4" />
              Default Positions
            </Link>
          )}
          {canEdit && (
            <Link
              to={`/teams/${team.slug}/edit`}
              className={buttonVariants({ variant: "outline", size: "sm" })}
              data-action="edit"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit Team
            </Link>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {/* Coaches section (collapsible) */}
      <div className="flex flex-col gap-4 rounded-lg border border-border" data-region="coaches">
        <button
          type="button"
          className="flex w-full items-center justify-between px-4 pt-4 pb-2 text-left"
          onClick={() => setCoachesOpen((v) => !v)}
        >
          <div className="flex items-center gap-2">
            {coachesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <h2 className="text-lg font-medium">Coaches ({coaches.length})</h2>
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); openAddCoach(); }}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Assign Coach
            </Button>
          )}
        </button>

        {coachesOpen && (
          <div className="px-4 pb-4">
            {coaches.length === 0 && (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-8">
                <p className="text-muted-foreground">No coaches assigned yet</p>
              </div>
            )}

            {coaches.length === 1 && (
              <div className="flex items-center justify-between">
                <Link to={`/coaches/${coaches[0].coach_id}`} className="font-medium hover:underline">{coaches[0].user_email}</Link>
                <span className="text-sm text-muted-foreground">
                  Assigned {new Date(coaches[0].created_at).toLocaleDateString()}
                </span>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveCoach(coaches[0].coach_id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            )}

            {/* 203 -- one columns array drives the sm+ table and the <sm cards. */}
            {coaches.length > 1 && (
              <ResponsiveTable
                columns={[
                  {
                    key: "email",
                    header: "Email",
                    cardRole: "title" as const,
                    className: "font-medium",
                    render: (coach: (typeof coaches)[number]) => (
                      <Link to={`/coaches/${coach.coach_id}`} className="hover:underline">{coach.user_email}</Link>
                    ),
                  },
                  {
                    key: "assigned",
                    header: "Assigned",
                    className: "text-muted-foreground",
                    render: (coach: (typeof coaches)[number]) => new Date(coach.created_at).toLocaleDateString(),
                  },
                  ...(canEdit
                    ? [{
                        key: "actions",
                        header: "",
                        cardRole: "action" as const,
                        headerClassName: "w-16",
                        render: (coach: (typeof coaches)[number]) => (
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveCoach(coach.coach_id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        ),
                      }]
                    : []),
                ]}
                rows={coaches}
                rowKey={(coach) => coach.id}
              />
            )}
          </div>
        )}
      </div>

      {/* Roster section (collapsible) */}
      <div className="flex flex-col gap-4 rounded-lg border border-border" data-region="roster">
        <button
          type="button"
          className="flex w-full items-center justify-between px-4 pt-4 pb-2 text-left"
          onClick={() => setRosterOpen((v) => !v)}
        >
          <div className="flex items-center gap-2">
            {rosterOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <h2 className="text-lg font-medium">Roster ({roster.length})</h2>
          </div>
          {canManageRoster && (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); openAddPlayer(); }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Player
            </Button>
          )}
        </button>

        {rosterOpen && (
          <div className="px-4 pb-4">
            {roster.length === 0 && (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-8">
                <p className="text-muted-foreground">No players on this team yet</p>
                {canManageRoster && (
                  <Button variant="outline" size="sm" onClick={openAddPlayer}>
                    Add players
                  </Button>
                )}
              </div>
            )}

            {/* 203 -- one columns array drives the sm+ table and the <sm cards
                (the 159 right-click profile shortcut stays desktop-only; the
                always-visible name link covers touch). */}
            {roster.length > 0 && (
              <ResponsiveTable
                columns={[
                  {
                    key: "name",
                    header: "Name",
                    cardRole: "title" as const,
                    className: "font-medium",
                    render: (player: (typeof roster)[number]) => (
                      <Link
                        to={`/players/${player.player_id}/edit`}
                        className="inline-flex items-center gap-1.5 hover:underline"
                        data-action="player-profile"
                      >
                        <UserCircle className="h-4 w-4 text-muted-foreground" aria-hidden />
                        {player.player_name}
                      </Link>
                    ),
                  },
                  { key: "dob", header: "Date of Birth", className: "text-muted-foreground", render: (player: (typeof roster)[number]) => player.date_of_birth },
                  { key: "family_code", header: "Family Code", className: "text-muted-foreground", render: (player: (typeof roster)[number]) => player.family_code ?? "--" },
                  { key: "season", header: "Season", className: "text-muted-foreground", render: (player: (typeof roster)[number]) => String(player.season) },
                  {
                    key: "actions",
                    header: "",
                    cardRole: "action" as const,
                    headerClassName: "w-24",
                    render: (player: (typeof roster)[number]) => (
                      <div className="flex items-center gap-1">
                        {/* 159 -- match-day emergency contact + medical quick view */}
                        <EmergencyContactButton playerId={player.player_id} playerName={player.player_name} />
                        {canSendMessage && (
                          <SendMessageButton
                            contextType="player"
                            contextId={player.player_id}
                            contextLabel={player.player_name}
                          />
                        )}
                        {canManageRoster && (
                          <Button variant="ghost" size="sm" onClick={() => handleRemovePlayer(player.player_id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    ),
                  },
                ]}
                rows={roster}
                rowKey={(player) => player.id}
              />
            )}
          </div>
        )}
      </div>

      {/* Add Player Dialog (inline) */}
      {showAddPlayer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold">Add Player to Team</h3>
            {availablePlayers.length === 0 ? (
              <p className="text-muted-foreground">No available players to add.</p>
            ) : (
              <div className="flex flex-col gap-4">
                <select
                  aria-label="Select a player"
                  value={selectedPlayerId}
                  onChange={(e) => setSelectedPlayerId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select a player...</option>
                  {availablePlayers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {playerDisplayName(p.first_name, p.surname)} ({p.date_of_birth})
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <Button onClick={handleAddPlayer} disabled={!selectedPlayerId}>
                    Add Player
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddPlayer(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {availablePlayers.length === 0 && (
              <div className="mt-4">
                <Button variant="outline" onClick={() => setShowAddPlayer(false)}>
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Coach Dialog (inline) */}
      {showAddCoach && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold">Assign Coach to Team</h3>
            {availableCoaches.length === 0 ? (
              <p className="text-muted-foreground">No available coaches to assign.</p>
            ) : (
              <div className="flex flex-col gap-4">
                <select
                  aria-label="Select a coach"
                  value={selectedCoachId}
                  onChange={(e) => setSelectedCoachId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select a coach...</option>
                  {availableCoaches.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.user_email}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <Button onClick={handleAddCoach} disabled={!selectedCoachId}>
                    Assign Coach
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddCoach(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {availableCoaches.length === 0 && (
              <div className="mt-4">
                <Button variant="outline" onClick={() => setShowAddCoach(false)}>
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Publish to Event Dialog (108) */}
      {showPublish && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4" data-region="publish-dialog">
          <div className="w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-lg">
            <h3 className="mb-1 text-lg font-semibold">Publish team info to an event</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              Parents on this event will see your message at the top of the event page. Publishing
              again creates a new version -- your previous publications are kept in history.
            </p>
            {publishableEvents.length === 0 ? (
              <>
                <p className="text-muted-foreground">
                  No upcoming events this team is on in the next 30 days. Add the team to a match first.
                </p>
                <div className="mt-4">
                  <Button variant="outline" onClick={() => setShowPublish(false)}>
                    Close
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label htmlFor="publish-event" className="text-sm font-medium">Event</label>
                  <select
                    id="publish-event"
                    aria-label="Select an event"
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select an event...</option>
                    {publishableEvents.map((e) => (
                      <option key={e.id} value={e.id}>
                        {new Date(e.starts_at).toLocaleDateString()} -- {e.name}
                        {e.status === "draft" ? " (draft)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="publish-content" className="text-sm font-medium">Message</label>
                  <textarea
                    id="publish-content"
                    aria-label="Publication content"
                    value={publishContent}
                    onChange={(e) => setPublishContent(e.target.value)}
                    rows={8}
                    placeholder="e.g. Warmup 08:30, kick-off 09:00. Away kit. Meet at Field 3."
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                {publishError && (
                  <p className="text-sm text-destructive" data-error>{publishError}</p>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={handlePublish}
                    disabled={!selectedEventId || !publishContent.trim() || publishing}
                    data-action="confirm-publish"
                  >
                    {publishing ? "Publishing..." : "Publish"}
                  </Button>
                  <Button variant="outline" onClick={() => setShowPublish(false)} disabled={publishing}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
